/**
 * Dashboard views — one for the whole practice, one for a single client.
 * @file src/web/features/main/pages/dashboard-views.tsx
 *
 * The dashboard used to render the same thing either way: totals summed across
 * every client. That number answers nothing. 14,000 vouchers spread over seven
 * clients tells a CA neither how much work there is nor which client to open.
 *
 * So the two scopes get two different screens, because they answer two
 * different questions:
 *
 * All entities → "which client needs me today?" A worklist, sorted by
 * whoever is most blocked, with the reason on the row.
 *
 * One entity → "what is the state of this client, and what do I do next?"
 * Compliance readiness first, books health second, and the
 * next action spelled out.
 *
 * Both lead with what is wrong rather than what is merely large. A green tile
 * showing 14,422 vouchers is decoration; "no document series — Table 13 cannot
 * be built" is work.
 */

import { Link } from 'react-router-dom';
import {
  AlertTriangle, CheckCircle2, ArrowRight, Building2, Scale, Upload,
  ClipboardCheck, Landmark, ReceiptText, Clock,
} from 'lucide-react';

const inr = (n: number): string => {
  if (Math.abs(n) >= 1e7) return `₹${(n / 1e7).toFixed(2)} Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n / 1e5).toFixed(2)} L`;
  return `₹${n.toLocaleString('en-IN')}`;
};
const num = (n: number) => n.toLocaleString('en-IN');

/* ══════════════════════════════════════════════════════════════════════
   All entities — the practice worklist
   ══════════════════════════════════════════════════════════════════════ */

export interface PortfolioClient {
  entityId: string; name: string; gstin: string | null;
  vouchers: number; ledgers: number;
  balanced: boolean | null; debits: number; credits: number;
  lastImportAt: string | null; staleDays: number | null;
  blockers: string[]; warnings: string[];
}
export interface Portfolio {
  clients: PortfolioClient[];
  totals: { clients: number; needAttention: number; unbalanced: number; neverImported: number; hsnMasterLoaded: boolean };
}

export function PortfolioView({ data, onPick }: { data: Portfolio; onPick: (entityId: string) => void }) {
  const { totals } = data;
  // Most blocked first: the list IS the priority order, so it does not need
  // sorting by hand every morning.
  const clients = [...data.clients].sort((a, b) =>
    (b.blockers.length - a.blockers.length) || (b.warnings.length - a.warnings.length) || a.name.localeCompare(b.name));

  const allClear = totals.needAttention === 0;

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Headline
          label="Clients needing attention"
          value={String(totals.needAttention)}
          of={`of ${totals.clients}`}
          tone={totals.needAttention ? 'danger' : 'ok'}
          hint={totals.needAttention ? 'blocked from filing' : 'nothing blocking'}
        />
        <Headline label="Books out of balance" value={String(totals.unbalanced)} tone={totals.unbalanced ? 'danger' : 'ok'} hint="debits ≠ credits" />
        <Headline label="Awaiting first import" value={String(totals.neverImported)} tone={totals.neverImported ? 'warn' : 'neutral'} hint="no books yet" />
        <Headline
          label="HSN master"
          value={totals.hsnMasterLoaded ? 'Loaded' : 'Empty'}
          tone={totals.hsnMasterLoaded ? 'ok' : 'warn'}
          hint="affects Table 12 for every client"
        />
      </div>

      {allClear && (
        <div className="flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50 px-6 py-4 text-sm text-ok-800">
          <CheckCircle2 className="h-4 w-4" />
          Nothing is blocking a return across your clients.
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-ink-200 bg-paper">
        <div className="border-b border-ink-100 px-6 py-5">
          <h3 className="text-base font-semibold text-ink-900">Clients</h3>
          <p className="mt-1 text-sm text-ink-500">
            Ordered by how much is blocking them, so the top of this list is where to start.
          </p>
        </div>
        <ul className="divide-y divide-ink-100">
          {clients.map((c) => <ClientRow key={c.entityId} c={c} onPick={onPick} />)}
        </ul>
      </div>
    </div>
  );
}

function ClientRow({ c, onPick }: { c: PortfolioClient; onPick: (id: string) => void }) {
  const state = c.blockers.length ? 'blocked' : c.warnings.length ? 'warn' : 'ok';
  const dot = { blocked: 'bg-danger-500', warn: 'bg-warn-400', ok: 'bg-ok-500' }[state];

  return (
    <li className="px-6 py-4 transition-colors hover:bg-ink-50/60">
      <div className="flex flex-wrap items-start gap-3">
        <span className={`mt-1.5 size-2 shrink-0 rounded-full ${dot}`} aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <button
              onClick={() => onPick(c.entityId)}
              className="text-sm font-semibold text-ink-900 hover:text-inkblack"
            >
              {c.name}
            </button>
            {c.gstin
              ? <span className="font-mono text-[11px] text-ink-400">{c.gstin}</span>
              : <span className="text-[11px] font-medium text-warn-600">no GSTIN</span>}
          </div>

          <p className="mt-0.5 text-xs text-ink-500">
            {c.vouchers ? `${num(c.vouchers)} vouchers · ${num(c.ledgers)} ledgers` : 'No books imported'}
            {c.balanced === true && <span className="text-ok-600"> · balanced</span>}
            {c.balanced === false && <span className="text-danger-600"> · out of balance by {inr(Math.abs(c.debits - c.credits))}</span>}
            {c.staleDays != null && <span> · imported {c.staleDays === 0 ? 'today' : `${c.staleDays}d ago`}</span>}
          </p>

          {(c.blockers.length > 0 || c.warnings.length > 0) && (
            <ul className="mt-2 space-y-1">
              {c.blockers.map((b) => (
                <li key={b} className="flex items-start gap-1.5 text-xs text-danger-700">
                  <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{b}
                </li>
              ))}
              {c.warnings.map((w) => (
                <li key={w} className="flex items-start gap-1.5 text-xs text-warn-700">
                  <Clock className="mt-0.5 h-3 w-3 shrink-0" />{w}
                </li>
              ))}
            </ul>
          )}
        </div>

        <button
          onClick={() => onPick(c.entityId)}
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-[11px] font-semibold text-ink-700 transition hover:bg-ink-50"
        >
          Open <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </li>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Single client
   ══════════════════════════════════════════════════════════════════════ */

export interface ClientSummary {
  totals: { companies: number; ledgers: number; vouchers: number; entries: number };
  reconciliation: { debits: number; credits: number; balanced: boolean };
  recentImports: Array<{ id: string; companyName: string; kind: string; vouchers: number; entries: number; ledgers: number; groups: number; status: string; createdAt: string }>;
}
export interface GapSummary {
  entityName: string;
  summary: { total: number; ok: number; gaps: number; blocking: number; phase2: number };
  sections: { key: string; label: string; items: { code: string; label: string; status: string; severity: string; detail: string; fixTab: string | null }[] }[];
}

export function ClientView({ summary, gaps }: { summary: ClientSummary; gaps: GapSummary | null }) {
  const recon = summary.reconciliation;
  const pct = gaps?.summary.total ? Math.round((gaps.summary.ok / gaps.summary.total) * 100) : null;

  // The blocking items, flattened — this is the "what do I do next" list.
  const blocking = (gaps?.sections ?? [])
    .flatMap((s) => s.items)
    .filter((i) => i.status === 'gap' && i.severity === 'blocking');

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Headline
          label="Books"
          value={recon.balanced ? 'Balanced' : 'Out of balance'}
          tone={recon.balanced ? 'ok' : 'danger'}
          hint={recon.balanced ? `${inr(recon.debits)} both sides` : `off by ${inr(Math.abs(recon.debits - recon.credits))}`}
        />
        <Headline
          label="Ready to file"
          value={gaps ? (gaps.summary.blocking === 0 ? 'Yes' : 'No') : '—'}
          tone={gaps && gaps.summary.blocking === 0 ? 'ok' : 'danger'}
          hint={gaps ? `${gaps.summary.blocking} blocker(s)` : 'checking'}
        />
        <Headline
          label="Master data"
          value={pct != null ? `${pct}%` : '—'}
          tone={pct != null && pct >= 80 ? 'ok' : 'warn'}
          hint={gaps ? `${gaps.summary.gaps} field(s) to fill` : ''}
        />
        <Headline label="Vouchers" value={num(summary.totals.vouchers)} tone="neutral" hint={`${num(summary.totals.ledgers)} ledgers`} />
      </div>

      {/* Next actions — the point of the screen. */}
      {blocking.length > 0 ? (
        <div className="overflow-hidden rounded-2xl border border-danger-200 bg-paper">
          <div className="border-b border-danger-100 bg-danger-50/60 px-6 py-4">
            <h3 className="flex items-center gap-2 text-base font-semibold text-danger-900">
              <AlertTriangle className="h-4 w-4" /> Do these before filing
            </h3>
            <p className="mt-1 text-sm text-danger-800">GSTN rejects the return without them.</p>
          </div>
          <ul className="divide-y divide-ink-100">
            {blocking.map((b) => (
              <li key={b.code} className="flex flex-wrap items-center gap-3 px-6 py-3.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink-900">{b.label}</p>
                  <p className="mt-0.5 text-xs text-ink-500">{b.detail}</p>
                </div>
                <Link
                  to="/admin/masters"
                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-ink-300 bg-paper px-3 py-1.5 text-[11px] font-semibold text-ink-700 no-underline transition hover:bg-ink-50"
                >
                  Fix <ArrowRight className="h-3 w-3" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : gaps && (
        <div className="flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50 px-6 py-4 text-sm text-ok-800">
          <CheckCircle2 className="h-4 w-4" />
          Nothing is blocking a return for {gaps.entityName}.
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <QuickLinks />
        <RecentImports imports={summary.recentImports} />
      </div>
    </div>
  );
}

function QuickLinks() {
  const links = [
    { to: '/admin/masters', icon: ClipboardCheck, label: 'Masters', desc: 'Identity, registrations, compliance' },
    { to: '/admin/gst', icon: Landmark, label: 'GST', desc: 'Registers, GSTR-1 and 3B' },
    { to: '/admin/books', icon: ReceiptText, label: 'Books', desc: 'Day book and ledgers' },
    { to: '/admin/import', icon: Upload, label: 'Import', desc: 'Bring in fresh Tally data' },
  ];
  return (
    <div className="rounded-2xl border border-ink-200 bg-paper p-6">
      <h3 className="text-base font-semibold text-ink-900">Go to</h3>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {links.map((l) => (
          <Link
            key={l.to} to={l.to}
            className="flex items-start gap-3 rounded-xl border border-ink-200 p-3 no-underline transition hover:border-ink-300 hover:bg-ink-50"
          >
            <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-highlighter-40 text-inkblack">
              <l.icon className="h-4 w-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold text-ink-900">{l.label}</span>
              <span className="block text-xs text-ink-500">{l.desc}</span>
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function RecentImports({ imports }: { imports: ClientSummary['recentImports'] }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-ink-200 bg-paper">
      <div className="border-b border-ink-100 px-6 py-5">
        <h3 className="text-base font-semibold text-ink-900">Recent imports</h3>
      </div>
      {imports.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-ink-500">
          No imports yet. <Link to="/admin/import" className="font-medium text-inkblack no-underline">Bring in Tally data</Link>.
        </div>
      ) : (
        <ul className="divide-y divide-ink-100">
          {imports.slice(0, 5).map((i) => (
            <li key={i.id} className="flex items-center gap-3 px-6 py-3">
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-ink-100 text-ink-500">
                <Building2 className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink-900">{i.companyName}</p>
                <p className="text-xs text-ink-500">
                  {num(i.vouchers)} vouchers · {num(i.ledgers)} ledgers · {new Date(i.createdAt).toLocaleDateString('en-IN')}
                </p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                i.status === 'loaded' ? 'bg-ok-50 text-ok-700' : 'bg-ink-100 text-ink-600'
              }`}>{i.status}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── shared ─────────────────────────────────────────────────────────── */

function Headline({ label, value, of, hint, tone }: {
  label: string; value: string; of?: string; hint?: string;
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
}) {
  const ring = {
    ok: 'border-ok-200 bg-ok-50/40', warn: 'border-warn-200 bg-warn-50/40',
    danger: 'border-danger-200 bg-danger-50/40', neutral: 'border-ink-200 bg-paper',
  }[tone];
  const text = {
    ok: 'text-ok-700', warn: 'text-warn-700', danger: 'text-danger-700', neutral: 'text-ink-900',
  }[tone];
  return (
    <div className={`rounded-2xl border p-5 ${ring}`}>
      <p className="text-xs font-medium uppercase tracking-wide text-ink-500">{label}</p>
      <p className={`mt-2 flex items-baseline gap-1.5 text-3xl font-semibold tabular-nums ${text}`}>
        {value}
        {of && <span className="text-sm font-medium text-ink-400">{of}</span>}
      </p>
      {hint && <p className="mt-1 text-xs text-ink-500">{hint}</p>}
    </div>
  );
}

export { Scale };
